/**
 * Passenger app strings.
 *
 * LINGALA IS A DRAFT AND NEEDS A NATIVE-SPEAKER REVIEW. It matters more here than
 * in the consoles: a member of staff who hits an odd phrase can work around it,
 * a passenger buying a ticket cannot.
 */
export const LOCALES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'sw', label: 'Kiswahili', short: 'SW' },
  { code: 'ln', label: 'Lingála', short: 'LN' },
] as const;

export type LocaleCode = (typeof LOCALES)[number]['code'];
export type StringKey = keyof (typeof dict)['en'];

export const dict = {
  en: {
    'ps.closeSafe': 'You can close this page — your ticket is emailed to you either way.',
    'ps.confirmed': 'Your seats are booked',
    'ps.confirmedHint': 'Your ticket is below, and a copy is on its way to your email.',
    'p.cancelPolicy': 'You can cancel a booking yourself up to {hours} hours before departure, and the refund goes back to your original payment method. Closer to departure, open your ticket and choose {action} — {operator} will review it.',
    'p.unpaidReleased': 'Unpaid bookings are released after {minutes} minutes so the seats go back on sale.',
    't.timesLocal': 'Times are local to the departure point ({tz}), not your device.',
    'm.linkSent': 'If that booking exists, we have sent a link to view it. The link works for 15 minutes.',
    'm.guestHint': 'Enter your booking reference and email and we will send you a link to your ticket.',
    'a.resetSent': 'If that address has an account, we have sent a link to choose a new password. It works once and expires in an hour.',
    'a.linkIncomplete': 'This link is incomplete. Open the link from your email, or ask for a new one.',
    'a.yourAccount': 'Your account',
    'p.contact': 'Contact {operator}',
    'h.footer': 'Times shown in {tz} · Fares in {currency} · Operated by {operator}',
    'p.faresTimes': 'Fares in {currency} · times in {tz}',
    'c.from': 'From',
    'c.to': 'To',
    'c.date': 'Date',
    'c.passengers': 'Passengers',
    'c.selectStop': 'Select a stop',
    'c.searchDepartures': 'Search departures',
    'c.noRoutes': 'No routes are published yet. Check back soon.',
    'c.email': 'Email',
    'c.password': 'Password',
    'c.yourName': 'Your name',
    'c.phone': 'Phone',
    'c.seats': 'Seats',
    'c.paid': 'Paid',
    'c.continue': 'Continue',
    'c.send': 'Send',
    'c.signIn': 'Sign in',
    'c.signOut': 'Sign out',
    'c.about': 'About',
    'c.help': 'Help',
    'c.myTrips': 'My trips',
    'c.departures': 'Departures',
    'c.language': 'Language',
    'c.loading': 'Loading',
    'h.whereTo': 'Where are you travelling?',
    'r.finding': 'Finding departures',
    'r.unavailable': 'Departures unavailable',
    'r.none': 'No departures found',
    'r.changeSearch': 'Change your search',
    's.loading': 'Loading seats',
    's.seatMap': 'Seat map',
    's.choose': 'Choose your seats',
    's.front': 'Front of bus',
    's.available': 'Available',
    's.yours': 'Yours',
    's.taken': 'Taken',
    'k.checkout': 'Checkout',
    'k.whoTravelling': 'Who is travelling?',
    'k.contact': 'Contact details',
    'k.fullName': 'Full name',
    'k.emailHint': 'We send your ticket here, and you will need it to find your booking later.',
    'k.phoneHint': 'Optional. Used only if there is a problem with your trip.',
    'k.payConfirm': 'Pay and confirm',
    'k.opening': 'Opening the payment page',
    'k.takingYou': 'Taking you to Baraka Pay',
    'k.handoverHint': 'Your seats are held. You will pay on Baraka Pay’s page, and this booking updates as soon as the payment is confirmed.',
    'k.continueToPay': 'Continue to Baraka Pay',
    'k.confirming': 'Confirming your payment',
    'k.confirmingHint': 'This usually takes a few seconds. Keep this page open.',
    't.loading': 'Loading your ticket',
    't.payFailed': 'That payment did not go through',
    't.payCancelled': 'Payment cancelled',
    't.tripCancelled': 'This departure was cancelled',
    't.bookingCancelled': 'Booking cancelled',
    't.stillHeld': 'Your seats are still held. You can try again before the hold expires.',
    't.heldLonger': 'Your seats are held a little longer if you want to finish paying.',
    't.autoUpdates': 'This page updates on its own once your payment is confirmed.',
    't.delayIncluded': 'The times below already include the delay.',
    't.codeAfterPay': 'Your boarding code appears once payment is confirmed.',
    't.offlineTicket': 'Saved ticket — you are offline',
    't.offlineAsOf': 'Your boarding codes still work. Trip status could not be checked; this is what we knew at {when}.',
    't.offlineNoStatus': 'Your boarding codes still work, but the trip status could not be checked.',
    't.offlineCodeHidden': 'Connect to the internet to show your boarding code.',
    't.cancelBooking': 'Cancel this booking',
    't.noLongerCancel': 'This booking can no longer be cancelled online.',
    't.reportProblem': 'Request a refund or report a problem',
    'm.loading': 'Loading your trips',
    'm.none': 'No bookings yet',
    'm.noneHint': 'Once you book a trip it will appear here.',
    'm.yourBookings': 'Your bookings',
    'm.upcoming': 'Upcoming',
    'm.past': 'Past',
    'm.findDeparture': 'Find a departure',
    'm.guest': 'Booked as a guest?',
    'm.emailTicket': 'Email me my ticket',
    'm.addToCalendar': 'Add to calendar',
    'm.bookAgain': 'Book again',
    'm.ticketSent': 'Ticket sent — check your inbox.',
    'm.ticketSendFailed': 'Could not send it. Try again in a moment.',
    'm.sending': 'Sending…',
    'm.emailTicketAction': 'Email ticket',
    'm.viewTicket': 'View ticket',
    'm.pastTrips': 'Past trips',
    'm.pastTripsHint': 'Journeys you have already made.',
    'm.sendTo': 'Send this ticket to',
    'm.recipientAccount': 'your account',
    'm.recipientBooking': 'on this booking',
    'm.recipientSomeoneElse': 'Someone else',
    'm.recipientEmail': 'Email address',
    'm.send': 'Send ticket',
    'm.cancel': 'Cancel',
    'm.sentTo': 'Sent to {to}',
    'm.recipientNeeded': 'Enter an email address.',
    'm.signInToForward': 'Sign in to the account that booked this trip to send its ticket to someone else.',
    'tr.title': 'Travellers',
    'tr.heading': 'People you book for',
    'tr.hint': 'Save the people you travel with and their names fill in at checkout.',
    'tr.none': 'No travellers saved',
    'tr.noneHint': 'Add someone below and their name is one tap away next time.',
    'tr.add': 'Add a traveller',
    'tr.save': 'Save traveller',
    'tr.remove': 'Remove',
    'tr.optional': 'Optional',
    'tr.idNumber': 'ID number',
    'tr.idHint': 'Optional. Only if your operator asks for one at boarding.',
    'tr.saveFailed': 'Could not save that. Try again.',
    'tr.signInNeeded': 'Sign in to save travellers',
    'tr.signInHint': 'Saved travellers belong to your account, so you will need one.',
    'tr.email': 'Email',
    'tr.emailHint': 'Optional. Only needed to forward them a ticket.',
    'm.reference': 'Booking reference',
    'm.checkEmail': 'Check your email',
    'a.min10': 'At least 10 characters.',
    'a.reset': 'Reset your password',
    'a.chooseNew': 'Choose a new password',
    'a.newPassword': 'New password',
    'a.createAccount': 'Create an account',
    'a.create': 'Create account',
    'a.forgot': 'Forgot your password?',
    'a.guestCheckout': 'Book without an account',
    'a.keepLooking': 'Keep looking',
    'ip.title': 'Add {app} to your home screen',
    'pr.title': 'Your profile',
    'k.promoCode': 'Promotion code (optional)',
    'k.promoHint': 'Have a code? Enter it and the discount is applied to this booking.',
    'o.title': 'Offers',
    'o.yours': 'Your offers',
    'o.yoursHint': 'Codes you have already claimed, and the rides they still cover.',
    'o.available': 'Available now',
    'o.availableHint': 'Claim a code and it is waiting the next time you book.',
    'o.none': 'No offers just yet',
    'o.noneHint': 'When this operator runs a promotion it will appear here.',
    'o.claim': 'Claim a code',
    'o.claimAction': 'Claim',
    'o.claimed': 'Claimed. It will apply to your next booking.',
    'o.claimFailed': 'That code could not be claimed.',
    'o.ridesLeft': '{count} rides left',
    'o.rideLeft': '1 ride left',
    'o.until': 'Until {date}',
    'o.expires': 'Use by {date}',
    'o.refer': 'Refer and earn',
    'o.referHint': 'Share your code. When a friend takes their first trip, you both benefit.',
    'o.yourCode': 'Your code',
    'o.copy': 'Copy',
    'o.copied': 'Copied',
    'o.waiting': '{count} joined, not yet travelled',
    'o.earned': '{count} earned',
    's.swap': 'Swap origin and destination',
    's.today': 'Today',
    'm.nextDeparture': 'Next departure',
    'm.moreUpcoming': '{count} more coming up',
    's.tomorrow': 'Tomorrow',
    's.errChooseBoth': 'Choose where you are travelling from and to.',
    's.errSameStop': 'Origin and destination must be different.',
    'pr.you': 'You',
    'pr.phone': 'Phone number',
    'pr.dob': 'Date of birth',
    'pr.idNumber': 'ID or passport number',
    'pr.idHint': 'Some operators ask for this at boarding.',
    'pr.address': 'Address',
    'pr.line1': 'Street address',
    'pr.line2': 'Apartment, suite (optional)',
    'pr.city': 'City',
    'pr.region': 'Province or region',
    'pr.postalCode': 'Postal code',
    'pr.country': 'Country',
    'pr.countryHint': 'Two-letter code, e.g. CD.',
    'pr.emergency': 'Emergency contact',
    'pr.emergencyHint': 'Who to reach if something happens on a journey.',
    'pr.emergencyName': 'Their name',
    'pr.emergencyPhone': 'Their phone number',
    'pr.save': 'Save profile',
    'pr.saved': 'Profile saved.',
    'pr.saveFailed': 'We could not save your profile. Try again.',
    'pr.addPhoto': 'Add a photo',
    'pr.changePhoto': 'Change photo',
    'pr.removePhoto': 'Remove',
    'pr.photoFailed': 'That photo could not be used. Try a PNG or JPEG under 2 MB.',
    'pr.signOut': 'Sign out',
    'ip.why': 'Opens straight to your tickets, and they still work with no signal.',
    'ip.install': 'Install',
    'ip.notNow': 'Not now',
    'ip.iosShare': 'Tap the Share button in the browser bar.',
    'ip.iosAdd': 'Choose “Add to Home Screen”.',
    'a.signInLead': 'Sign in to book, and to keep your trips together on any device.',
    'a.registerLead': 'You need an account to book. It only takes a moment.',
    'a.haveAccount': 'I already have an account',
    'm.claim': 'Add an earlier booking',
    'm.claimHint': 'Booked before you had an account? Enter the reference and the email it was sent to.',
    'm.claimAction': 'Add to my trips',
    'm.claimed': 'Added to your trips.',
    'm.claimNotFound': 'No booking matches that reference and email.',
    'm.signInToSee': 'Sign in to see your trips',
    'm.signInToSeeHint': 'Your bookings are kept with your account, on any device.',
    'a.backToSignIn': 'Back to sign in',
    'a.emailLink': 'Email me a link',
    'a.saveNew': 'Save new password',
    'a.signsOut': 'Changing your password signs you out everywhere else.',
    'd.report': 'Report a problem',
    'd.addMessage': 'Add a message',
    'd.about': 'What is this about?',
    'd.subject': 'Subject',
    'd.tellThem': 'Tell them what happened',
    'd.newRequest': 'New request',
    'd.wentWrong': 'Something went wrong',
    'd.somethingElse': 'Something else',
    'd.sendRequest': 'Send request',
    'd.requestRefund': 'Request a refund',
    'p.cancellations': 'Cancellations and refunds',
    'p.questions': 'Questions',
    'p.findingBooking': 'Finding a booking',
    'a.account': 'Account: {name}',
    'a.signInShort': 'Keep your trips together',
    'n.openMenu': 'Open menu',
    'n.closeMenu': 'Close menu',
    'pp.getReminders': 'Get departure reminders',
    'pp.turnOn': 'Turn on reminders',
    'pp.notNow': 'Not now',
    'pp.willRemind': 'You will get a reminder before departure.',
    'mt.noSite': 'No booking site at this address',
    'mt.checkAddress': 'Check the web address, or use the link your bus company gave you.',
    'mt.offline': 'You are offline',
    'mt.offlineHint': 'Connect to the internet to load this booking site for the first time.',
    'mt.paused': 'Booking is paused right now. Tickets you already hold remain valid.',
    'mt.checkBack': 'Please check back shortly.',
    'mt.beingSetUp': 'This booking site is still being set up.',
  },

  fr: {
    'ps.closeSafe': 'Vous pouvez fermer cette page — votre billet vous est envoyé par e-mail dans tous les cas.',
    'ps.confirmed': 'Vos places sont réservées',
    'ps.confirmedHint': 'Votre billet est ci-dessous, et une copie part vers votre e-mail.',
    'p.cancelPolicy': 'Vous pouvez annuler vous-même jusqu’à {hours} heures avant le départ, et le remboursement revient sur votre moyen de paiement d’origine. Plus près du départ, ouvrez votre billet et choisissez {action} — {operator} l’examinera.',
    'p.unpaidReleased': 'Les réservations impayées sont libérées après {minutes} minutes et les places sont remises en vente.',
    't.timesLocal': 'Les heures sont celles du point de départ ({tz}), pas celles de votre appareil.',
    'm.linkSent': 'Si cette réservation existe, nous avons envoyé un lien pour la consulter. Il est valable 15 minutes.',
    'm.guestHint': 'Saisissez votre référence de réservation et votre e-mail : nous vous enverrons un lien vers votre billet.',
    'a.resetSent': 'Si cette adresse a un compte, nous avons envoyé un lien pour choisir un nouveau mot de passe. Il est à usage unique et expire dans une heure.',
    'a.linkIncomplete': 'Ce lien est incomplet. Ouvrez le lien depuis votre e-mail, ou demandez-en un nouveau.',
    'a.yourAccount': 'Votre compte',
    'p.contact': 'Contacter {operator}',
    'h.footer': 'Heures en {tz} · Tarifs en {currency} · Opéré par {operator}',
    'p.faresTimes': 'Tarifs en {currency} · heures en {tz}',
    'c.from': 'De',
    'c.to': 'À',
    'c.date': 'Date',
    'c.passengers': 'Passagers',
    'c.selectStop': 'Choisir un arrêt',
    'c.searchDepartures': 'Rechercher les départs',
    'c.noRoutes': 'Aucune ligne publiée pour le moment. Revenez bientôt.',
    'c.email': 'E-mail',
    'c.password': 'Mot de passe',
    'c.yourName': 'Votre nom',
    'c.phone': 'Téléphone',
    'c.seats': 'Places',
    'c.paid': 'Payé',
    'c.continue': 'Continuer',
    'c.send': 'Envoyer',
    'c.signIn': 'Se connecter',
    'c.signOut': 'Se déconnecter',
    'c.about': 'À propos',
    'c.help': 'Aide',
    'c.myTrips': 'Mes trajets',
    'c.departures': 'Départs',
    'c.language': 'Langue',
    'c.loading': 'Chargement',
    'h.whereTo': 'Où voyagez-vous ?',
    'r.finding': 'Recherche des départs',
    'r.unavailable': 'Départs indisponibles',
    'r.none': 'Aucun départ trouvé',
    'r.changeSearch': 'Modifiez votre recherche',
    's.loading': 'Chargement des places',
    's.seatMap': 'Plan des places',
    's.choose': 'Choisissez vos places',
    's.front': 'Avant du bus',
    's.available': 'Libre',
    's.yours': 'Vos places',
    's.taken': 'Occupé',
    'k.checkout': 'Paiement',
    'k.whoTravelling': 'Qui voyage ?',
    'k.contact': 'Coordonnées',
    'k.fullName': 'Nom complet',
    'k.emailHint': 'Nous y envoyons votre billet ; il vous servira à retrouver votre réservation.',
    'k.phoneHint': 'Facultatif. Utilisé uniquement en cas de problème.',
    'k.payConfirm': 'Payer et confirmer',
    'k.opening': 'Ouverture de la page de paiement',
    'k.takingYou': 'Redirection vers Baraka Pay',
    'k.handoverHint': 'Vos places sont réservées. Vous paierez sur la page de Baraka Pay, et cette réservation se mettra à jour dès la confirmation.',
    'k.continueToPay': 'Continuer vers Baraka Pay',
    'k.confirming': 'Confirmation de votre paiement',
    'k.confirmingHint': 'Cela prend généralement quelques secondes. Gardez cette page ouverte.',
    't.loading': 'Chargement de votre billet',
    't.payFailed': 'Le paiement n’a pas abouti',
    't.payCancelled': 'Paiement annulé',
    't.tripCancelled': 'Ce départ a été annulé',
    't.bookingCancelled': 'Réservation annulée',
    't.stillHeld': 'Vos places sont toujours réservées. Vous pouvez réessayer avant expiration.',
    't.heldLonger': 'Vos places restent réservées un moment si vous souhaitez terminer le paiement.',
    't.autoUpdates': 'Cette page se met à jour automatiquement dès la confirmation du paiement.',
    't.delayIncluded': 'Les heures ci-dessous incluent déjà le retard.',
    't.codeAfterPay': 'Votre code d’embarquement apparaît après confirmation du paiement.',
    't.offlineTicket': 'Billet enregistré — vous êtes hors ligne',
    't.offlineAsOf': 'Vos codes d’embarquement fonctionnent toujours. L’état du trajet n’a pas pu être vérifié ; voici ce que nous savions le {when}.',
    't.offlineNoStatus': 'Vos codes d’embarquement fonctionnent toujours, mais l’état du trajet n’a pas pu être vérifié.',
    't.offlineCodeHidden': 'Connectez-vous à Internet pour afficher votre code d’embarquement.',
    't.cancelBooking': 'Annuler cette réservation',
    't.noLongerCancel': 'Cette réservation ne peut plus être annulée en ligne.',
    't.reportProblem': 'Demander un remboursement ou signaler un problème',
    'm.loading': 'Chargement de vos trajets',
    'm.none': 'Aucune réservation',
    'm.noneHint': 'Dès que vous réservez, le trajet apparaîtra ici.',
    'm.yourBookings': 'Vos réservations',
    'm.upcoming': 'À venir',
    'm.past': 'Passés',
    'm.findDeparture': 'Trouver un départ',
    'm.guest': 'Réservé sans compte ?',
    'm.emailTicket': 'M’envoyer mon billet',
    'm.addToCalendar': 'Ajouter au calendrier',
    'm.bookAgain': 'Réserver à nouveau',
    'm.ticketSent': 'Billet envoyé — vérifiez votre boîte mail.',
    'm.ticketSendFailed': 'Envoi impossible. Réessayez dans un instant.',
    'm.sending': 'Envoi…',
    'm.emailTicketAction': 'Envoyer le billet',
    'm.viewTicket': 'Voir le billet',
    'm.pastTrips': 'Trajets passés',
    'm.pastTripsHint': 'Les trajets que vous avez déjà effectués.',
    'm.sendTo': 'Envoyer ce billet à',
    'm.recipientAccount': 'votre compte',
    'm.recipientBooking': 'sur cette réservation',
    'm.recipientSomeoneElse': 'Quelqu’un d’autre',
    'm.recipientEmail': 'Adresse e-mail',
    'm.send': 'Envoyer le billet',
    'm.cancel': 'Annuler',
    'm.sentTo': 'Envoyé à {to}',
    'm.recipientNeeded': 'Saisissez une adresse e-mail.',
    'm.signInToForward': 'Connectez-vous au compte qui a réservé ce trajet pour envoyer son billet à quelqu’un d’autre.',
    'tr.title': 'Voyageurs',
    'tr.heading': 'Les personnes pour qui vous réservez',
    'tr.hint': 'Enregistrez les personnes avec qui vous voyagez : leurs noms se remplissent au paiement.',
    'tr.none': 'Aucun voyageur enregistré',
    'tr.noneHint': 'Ajoutez quelqu’un ci-dessous et son nom sera à portée de clic.',
    'tr.add': 'Ajouter un voyageur',
    'tr.save': 'Enregistrer',
    'tr.remove': 'Retirer',
    'tr.optional': 'Facultatif',
    'tr.idNumber': 'Numéro de pièce',
    'tr.idHint': 'Facultatif. Seulement si votre transporteur le demande à l’embarquement.',
    'tr.saveFailed': 'Enregistrement impossible. Réessayez.',
    'tr.signInNeeded': 'Connectez-vous pour enregistrer des voyageurs',
    'tr.signInHint': 'Les voyageurs enregistrés appartiennent à votre compte.',
    'tr.email': 'E-mail',
    'tr.emailHint': 'Facultatif. Utile uniquement pour lui transmettre un billet.',
    'm.reference': 'Référence de réservation',
    'm.checkEmail': 'Vérifiez votre e-mail',
    'a.min10': 'Au moins 10 caractères.',
    'a.reset': 'Réinitialiser votre mot de passe',
    'a.chooseNew': 'Choisissez un nouveau mot de passe',
    'a.newPassword': 'Nouveau mot de passe',
    'a.createAccount': 'Créer un compte',
    'a.create': 'Créer le compte',
    'a.forgot': 'Mot de passe oublié ?',
    'a.guestCheckout': 'Réserver sans compte',
    'a.keepLooking': 'Continuer à chercher',
    'ip.title': 'Ajoutez {app} à votre écran d’accueil',
    'pr.title': 'Votre profil',
    'k.promoCode': 'Code promotionnel (facultatif)',
    'k.promoHint': 'Vous avez un code ? Saisissez-le et la remise s’applique à cette réservation.',
    'o.title': 'Offres',
    'o.yours': 'Vos offres',
    'o.yoursHint': 'Les codes déjà réclamés et les trajets qu’ils couvrent encore.',
    'o.available': 'Disponibles',
    'o.availableHint': 'Réclamez un code et il vous attendra à la prochaine réservation.',
    'o.none': 'Aucune offre pour l’instant',
    'o.noneHint': 'Dès que ce transporteur lance une promotion, elle apparaîtra ici.',
    'o.claim': 'Réclamer un code',
    'o.claimAction': 'Réclamer',
    'o.claimed': 'Réclamé. La remise s’appliquera à votre prochaine réservation.',
    'o.claimFailed': 'Ce code n’a pas pu être réclamé.',
    'o.ridesLeft': '{count} trajets restants',
    'o.rideLeft': '1 trajet restant',
    'o.until': 'Jusqu’au {date}',
    'o.expires': 'À utiliser avant le {date}',
    'o.refer': 'Parrainez et gagnez',
    'o.referHint': 'Partagez votre code. Au premier trajet de votre ami, vous y gagnez tous les deux.',
    'o.yourCode': 'Votre code',
    'o.copy': 'Copier',
    'o.copied': 'Copié',
    'o.waiting': '{count} inscrits, pas encore voyagé',
    'o.earned': '{count} gagnés',
    's.swap': 'Inverser le départ et l’arrivée',
    's.today': 'Aujourd’hui',
    'm.nextDeparture': 'Prochain départ',
    'm.moreUpcoming': '{count} autres à venir',
    's.tomorrow': 'Demain',
    's.errChooseBoth': 'Choisissez votre point de départ et votre destination.',
    's.errSameStop': 'Le départ et l’arrivée doivent être différents.',
    'pr.you': 'Vous',
    'pr.phone': 'Numéro de téléphone',
    'pr.dob': 'Date de naissance',
    'pr.idNumber': 'Numéro de pièce d’identité ou de passeport',
    'pr.idHint': 'Certains transporteurs le demandent à l’embarquement.',
    'pr.address': 'Adresse',
    'pr.line1': 'Adresse',
    'pr.line2': 'Appartement, bureau (facultatif)',
    'pr.city': 'Ville',
    'pr.region': 'Province ou région',
    'pr.postalCode': 'Code postal',
    'pr.country': 'Pays',
    'pr.countryHint': 'Code à deux lettres, par ex. CD.',
    'pr.emergency': 'Contact d’urgence',
    'pr.emergencyHint': 'Qui joindre s’il arrive quelque chose pendant un trajet.',
    'pr.emergencyName': 'Son nom',
    'pr.emergencyPhone': 'Son numéro de téléphone',
    'pr.save': 'Enregistrer le profil',
    'pr.saved': 'Profil enregistré.',
    'pr.saveFailed': 'Nous n’avons pas pu enregistrer votre profil. Réessayez.',
    'pr.addPhoto': 'Ajouter une photo',
    'pr.changePhoto': 'Changer la photo',
    'pr.removePhoto': 'Supprimer',
    'pr.photoFailed': 'Cette photo n’a pas pu être utilisée. Essayez un PNG ou JPEG de moins de 2 Mo.',
    'pr.signOut': 'Se déconnecter',
    'ip.why': 'Ouvre directement vos billets, qui fonctionnent même sans réseau.',
    'ip.install': 'Installer',
    'ip.notNow': 'Plus tard',
    'ip.iosShare': 'Touchez le bouton Partager dans la barre du navigateur.',
    'ip.iosAdd': 'Choisissez « Sur l’écran d’accueil ».',
    'a.signInLead': 'Connectez-vous pour réserver et retrouver vos trajets sur tous vos appareils.',
    'a.registerLead': 'Un compte est nécessaire pour réserver. Cela ne prend qu’un instant.',
    'a.haveAccount': 'J’ai déjà un compte',
    'm.claim': 'Ajouter une réservation antérieure',
    'm.claimHint': 'Réservé avant d’avoir un compte ? Saisissez la référence et l’e-mail utilisé.',
    'm.claimAction': 'Ajouter à mes trajets',
    'm.claimed': 'Ajouté à vos trajets.',
    'm.claimNotFound': 'Aucune réservation ne correspond à cette référence et à cet e-mail.',
    'm.signInToSee': 'Connectez-vous pour voir vos trajets',
    'm.signInToSeeHint': 'Vos réservations sont liées à votre compte, sur tous vos appareils.',
    'a.backToSignIn': 'Retour à la connexion',
    'a.emailLink': 'M’envoyer un lien',
    'a.saveNew': 'Enregistrer le nouveau mot de passe',
    'a.signsOut': 'Changer votre mot de passe vous déconnecte partout ailleurs.',
    'd.report': 'Signaler un problème',
    'd.addMessage': 'Ajouter un message',
    'd.about': 'De quoi s’agit-il ?',
    'd.subject': 'Objet',
    'd.tellThem': 'Expliquez ce qui s’est passé',
    'd.newRequest': 'Nouvelle demande',
    'd.wentWrong': 'Un problème est survenu',
    'd.somethingElse': 'Autre chose',
    'd.sendRequest': 'Envoyer la demande',
    'd.requestRefund': 'Demander un remboursement',
    'p.cancellations': 'Annulations et remboursements',
    'p.questions': 'Questions',
    'p.findingBooking': 'Retrouver une réservation',
    'a.account': 'Compte : {name}',
    'a.signInShort': 'Gardez vos trajets ensemble',
    'n.openMenu': 'Ouvrir le menu',
    'n.closeMenu': 'Fermer le menu',
    'pp.getReminders': 'Recevoir des rappels de départ',
    'pp.turnOn': 'Activer les rappels',
    'pp.notNow': 'Plus tard',
    'pp.willRemind': 'Vous recevrez un rappel avant le départ.',
    'mt.noSite': 'Aucun site de réservation à cette adresse',
    'mt.checkAddress': 'Vérifiez l’adresse, ou utilisez le lien fourni par votre compagnie.',
    'mt.offline': 'Vous êtes hors ligne',
    'mt.offlineHint': 'Connectez-vous à Internet pour charger ce site pour la première fois.',
    'mt.paused': 'Les réservations sont suspendues. Les billets déjà émis restent valables.',
    'mt.checkBack': 'Merci de revenir sous peu.',
    'mt.beingSetUp': 'Ce site de réservation est encore en configuration.',
  },

  sw: {
    'ps.closeSafe': 'Unaweza kufunga ukurasa huu — tikiti yako inatumwa kwa barua pepe vyovyote vile.',
    'ps.confirmed': 'Viti vyako vimewekwa',
    'ps.confirmedHint': 'Tikiti yako iko hapa chini, na nakala inakuja kwenye barua pepe yako.',
    'p.cancelPolicy': 'Unaweza kughairi nafasi mwenyewe hadi saa {hours} kabla ya kuondoka, na fedha hurejeshwa kwenye njia yako ya awali ya malipo. Karibu na kuondoka, fungua tikiti yako na uchague {action} — {operator} atapitia.',
    'p.unpaidReleased': 'Nafasi zisizolipiwa huachiwa baada ya dakika {minutes} ili viti virudi sokoni.',
    't.timesLocal': 'Nyakati ni za mahali pa kuondoka ({tz}), si za kifaa chako.',
    'm.linkSent': 'Ikiwa nafasi hiyo ipo, tumetuma kiungo cha kuiona. Kiungo hufanya kazi kwa dakika 15.',
    'm.guestHint': 'Weka kumbukumbu ya nafasi yako na barua pepe, nasi tutakutumia kiungo cha tikiti yako.',
    'a.resetSent': 'Ikiwa anwani hiyo ina akaunti, tumetuma kiungo cha kuchagua nenosiri jipya. Hufanya kazi mara moja na huisha baada ya saa moja.',
    'a.linkIncomplete': 'Kiungo hiki hakijakamilika. Fungua kiungo kutoka barua pepe yako, au omba kipya.',
    'a.yourAccount': 'Akaunti yako',
    'p.contact': 'Wasiliana na {operator}',
    'h.footer': 'Nyakati za {tz} · Nauli kwa {currency} · Inaendeshwa na {operator}',
    'p.faresTimes': 'Nauli kwa {currency} · nyakati za {tz}',
    'c.from': 'Kutoka',
    'c.to': 'Hadi',
    'c.date': 'Tarehe',
    'c.passengers': 'Abiria',
    'c.selectStop': 'Chagua kituo',
    'c.searchDepartures': 'Tafuta safari',
    'c.noRoutes': 'Hakuna njia zilizochapishwa bado. Rudi hivi karibuni.',
    'c.email': 'Barua pepe',
    'c.password': 'Nenosiri',
    'c.yourName': 'Jina lako',
    'c.phone': 'Simu',
    'c.seats': 'Viti',
    'c.paid': 'Imelipwa',
    'c.continue': 'Endelea',
    'c.send': 'Tuma',
    'c.signIn': 'Ingia',
    'c.signOut': 'Toka',
    'c.about': 'Kuhusu',
    'c.help': 'Msaada',
    'c.myTrips': 'Safari zangu',
    'c.departures': 'Safari',
    'c.language': 'Lugha',
    'c.loading': 'Inapakia',
    'h.whereTo': 'Unasafiri wapi?',
    'r.finding': 'Inatafuta safari',
    'r.unavailable': 'Safari hazipatikani',
    'r.none': 'Hakuna safari iliyopatikana',
    'r.changeSearch': 'Badilisha utafutaji wako',
    's.loading': 'Inapakia viti',
    's.seatMap': 'Ramani ya viti',
    's.choose': 'Chagua viti vyako',
    's.front': 'Mbele ya basi',
    's.available': 'Kipo',
    's.yours': 'Vyako',
    's.taken': 'Kimechukuliwa',
    'k.checkout': 'Malipo',
    'k.whoTravelling': 'Nani anasafiri?',
    'k.contact': 'Mawasiliano',
    'k.fullName': 'Jina kamili',
    'k.emailHint': 'Tunatuma tikiti yako hapa, na utaihitaji kupata nafasi yako baadaye.',
    'k.phoneHint': 'Si lazima. Hutumika tu ikiwa kuna tatizo na safari yako.',
    'k.payConfirm': 'Lipa na uthibitishe',
    'k.opening': 'Inafungua ukurasa wa malipo',
    'k.takingYou': 'Tunakupeleka Baraka Pay',
    'k.handoverHint': 'Viti vyako vimehifadhiwa. Utalipa kwenye ukurasa wa Baraka Pay, na nafasi hii itasasishwa mara malipo yatakapothibitishwa.',
    'k.continueToPay': 'Endelea kwa Baraka Pay',
    'k.confirming': 'Inathibitisha malipo yako',
    'k.confirmingHint': 'Kwa kawaida huchukua sekunde chache. Weka ukurasa huu wazi.',
    't.loading': 'Inapakia tikiti yako',
    't.payFailed': 'Malipo hayo hayakufanikiwa',
    't.payCancelled': 'Malipo yamefutwa',
    't.tripCancelled': 'Safari hii imefutwa',
    't.bookingCancelled': 'Nafasi imefutwa',
    't.stillHeld': 'Viti vyako bado vimehifadhiwa. Unaweza kujaribu tena kabla ya muda kuisha.',
    't.heldLonger': 'Viti vyako vimehifadhiwa kwa muda kidogo zaidi ikiwa unataka kumaliza kulipa.',
    't.autoUpdates': 'Ukurasa huu unajisasisha wenyewe malipo yakithibitishwa.',
    't.delayIncluded': 'Nyakati zilizo hapa chini tayari zimejumuisha ucheleweshaji.',
    't.codeAfterPay': 'Msimbo wako wa kupanda unaonekana malipo yakithibitishwa.',
    't.offlineTicket': 'Tikiti iliyohifadhiwa — huna mtandao',
    't.offlineAsOf': 'Misimbo yako ya kupanda bado inafanya kazi. Hali ya safari haikuweza kuangaliwa; haya ndiyo tuliyojua {when}.',
    't.offlineNoStatus': 'Misimbo yako ya kupanda bado inafanya kazi, lakini hali ya safari haikuweza kuangaliwa.',
    't.offlineCodeHidden': 'Unganisha kwenye mtandao ili kuonyesha msimbo wako wa kupanda.',
    't.cancelBooking': 'Futa nafasi hii',
    't.noLongerCancel': 'Nafasi hii haiwezi tena kufutwa mtandaoni.',
    't.reportProblem': 'Omba marejesho au ripoti tatizo',
    'm.loading': 'Inapakia safari zako',
    'm.none': 'Hakuna nafasi bado',
    'm.noneHint': 'Ukishaweka nafasi, safari itaonekana hapa.',
    'm.yourBookings': 'Nafasi zako',
    'm.upcoming': 'Zijazo',
    'm.past': 'Zilizopita',
    'm.findDeparture': 'Tafuta safari',
    'm.guest': 'Uliweka nafasi kama mgeni?',
    'm.emailTicket': 'Nitumie tikiti yangu',
    'm.addToCalendar': 'Ongeza kwenye kalenda',
    'm.bookAgain': 'Weka nafasi tena',
    'm.ticketSent': 'Tikiti imetumwa — angalia barua pepe yako.',
    'm.ticketSendFailed': 'Imeshindwa kutuma. Jaribu tena baadaye.',
    'm.sending': 'Inatuma…',
    'm.emailTicketAction': 'Tuma tikiti',
    'm.viewTicket': 'Ona tikiti',
    'm.pastTrips': 'Safari zilizopita',
    'm.pastTripsHint': 'Safari ambazo tayari umezifanya.',
    'm.sendTo': 'Tuma tikiti hii kwa',
    'm.recipientAccount': 'akaunti yako',
    'm.recipientBooking': 'kwenye nafasi hii',
    'm.recipientSomeoneElse': 'Mtu mwingine',
    'm.recipientEmail': 'Anwani ya barua pepe',
    'm.send': 'Tuma tikiti',
    'm.cancel': 'Ghairi',
    'm.sentTo': 'Imetumwa kwa {to}',
    'm.recipientNeeded': 'Weka anwani ya barua pepe.',
    'm.signInToForward': 'Ingia kwenye akaunti iliyoweka nafasi hii ili kutuma tikiti yake kwa mtu mwingine.',
    'tr.title': 'Wasafiri',
    'tr.heading': 'Watu unaowawekea nafasi',
    'tr.hint': 'Hifadhi watu unaosafiri nao; majina yao yatajaa wakati wa malipo.',
    'tr.none': 'Hakuna msafiri aliyehifadhiwa',
    'tr.noneHint': 'Ongeza mtu hapa chini na jina lake litakuwa karibu wakati ujao.',
    'tr.add': 'Ongeza msafiri',
    'tr.save': 'Hifadhi',
    'tr.remove': 'Ondoa',
    'tr.optional': 'Si lazima',
    'tr.idNumber': 'Namba ya kitambulisho',
    'tr.idHint': 'Si lazima. Ni pale tu msafirishaji anapoiomba wakati wa kupanda.',
    'tr.saveFailed': 'Imeshindwa kuhifadhi. Jaribu tena.',
    'tr.signInNeeded': 'Ingia ili kuhifadhi wasafiri',
    'tr.signInHint': 'Wasafiri waliohifadhiwa ni wa akaunti yako.',
    'tr.email': 'Barua pepe',
    'tr.emailHint': 'Si lazima. Inahitajika tu ili kumtumia tikiti.',
    'm.reference': 'Kumbukumbu ya nafasi',
    'm.checkEmail': 'Angalia barua pepe yako',
    'a.min10': 'Angalau herufi 10.',
    'a.reset': 'Weka upya nenosiri lako',
    'a.chooseNew': 'Chagua nenosiri jipya',
    'a.newPassword': 'Nenosiri jipya',
    'a.createAccount': 'Fungua akaunti',
    'a.create': 'Fungua akaunti',
    'a.forgot': 'Umesahau nenosiri?',
    'a.guestCheckout': 'Weka nafasi bila akaunti',
    'a.keepLooking': 'Endelea kutafuta',
    'ip.title': 'Ongeza {app} kwenye skrini yako ya kwanza',
    'pr.title': 'Wasifu wako',
    'k.promoCode': 'Msimbo wa punguzo (si lazima)',
    'k.promoHint': 'Una msimbo? Weka hapa na punguzo litatumika kwenye nafasi hii.',
    'o.title': 'Ofa',
    'o.yours': 'Ofa zako',
    'o.yoursHint': 'Misimbo uliyochukua, na safari ambazo bado inashughulikia.',
    'o.available': 'Zinapatikana sasa',
    'o.availableHint': 'Chukua msimbo, utakusubiri utakapoweka nafasi tena.',
    'o.none': 'Hakuna ofa kwa sasa',
    'o.noneHint': 'Mwendeshaji akianzisha punguzo, litaonekana hapa.',
    'o.claim': 'Chukua msimbo',
    'o.claimAction': 'Chukua',
    'o.claimed': 'Imechukuliwa. Itatumika kwenye nafasi yako inayofuata.',
    'o.claimFailed': 'Msimbo huo haukuweza kuchukuliwa.',
    'o.ridesLeft': 'Safari {count} zimebaki',
    'o.rideLeft': 'Safari 1 imebaki',
    'o.until': 'Hadi {date}',
    'o.expires': 'Tumia kabla ya {date}',
    'o.refer': 'Alika na upate',
    'o.referHint': 'Shiriki msimbo wako. Rafiki yako akisafiri mara ya kwanza, nyote mnanufaika.',
    'o.yourCode': 'Msimbo wako',
    'o.copy': 'Nakili',
    'o.copied': 'Imenakiliwa',
    'o.waiting': '{count} wamejiunga, bado hawajasafiri',
    'o.earned': '{count} zimepatikana',
    's.swap': 'Badilisha mahali pa kuanzia na kuishia',
    's.today': 'Leo',
    'm.nextDeparture': 'Safari inayofuata',
    'm.moreUpcoming': 'Nyingine {count} zinakuja',
    's.tomorrow': 'Kesho',
    's.errChooseBoth': 'Chagua unakoanzia na unakoenda.',
    's.errSameStop': 'Mahali pa kuanzia na pa kuishia lazima pawe tofauti.',
    'pr.you': 'Wewe',
    'pr.phone': 'Namba ya simu',
    'pr.dob': 'Tarehe ya kuzaliwa',
    'pr.idNumber': 'Namba ya kitambulisho au pasipoti',
    'pr.idHint': 'Baadhi ya waendeshaji huiuliza wakati wa kupanda.',
    'pr.address': 'Anwani',
    'pr.line1': 'Anwani ya mtaa',
    'pr.line2': 'Nyumba, ofisi (si lazima)',
    'pr.city': 'Jiji',
    'pr.region': 'Jimbo au mkoa',
    'pr.postalCode': 'Namba ya posta',
    'pr.country': 'Nchi',
    'pr.countryHint': 'Herufi mbili, mfano CD.',
    'pr.emergency': 'Mtu wa dharura',
    'pr.emergencyHint': 'Nani apigiwe simu ikiwa jambo litatokea safarini.',
    'pr.emergencyName': 'Jina lake',
    'pr.emergencyPhone': 'Namba yake ya simu',
    'pr.save': 'Hifadhi wasifu',
    'pr.saved': 'Wasifu umehifadhiwa.',
    'pr.saveFailed': 'Hatukuweza kuhifadhi wasifu wako. Jaribu tena.',
    'pr.addPhoto': 'Ongeza picha',
    'pr.changePhoto': 'Badilisha picha',
    'pr.removePhoto': 'Ondoa',
    'pr.photoFailed': 'Picha hiyo haikuweza kutumika. Jaribu PNG au JPEG chini ya MB 2.',
    'pr.signOut': 'Ondoka',
    'ip.why': 'Hufungua tiketi zako moja kwa moja, na zinafanya kazi hata bila mtandao.',
    'ip.install': 'Sakinisha',
    'ip.notNow': 'Baadaye',
    'ip.iosShare': 'Gusa kitufe cha Share kwenye upau wa kivinjari.',
    'ip.iosAdd': 'Chagua “Add to Home Screen”.',
    'a.signInLead': 'Ingia ili kuweka nafasi na kuhifadhi safari zako katika kifaa chochote.',
    'a.registerLead': 'Unahitaji akaunti ili kuweka nafasi. Inachukua muda mfupi tu.',
    'a.haveAccount': 'Tayari nina akaunti',
    'm.claim': 'Ongeza nafasi ya awali',
    'm.claimHint': 'Uliweka nafasi kabla ya kuwa na akaunti? Weka kumbukumbu na barua pepe iliyotumika.',
    'm.claimAction': 'Ongeza kwenye safari zangu',
    'm.claimed': 'Imeongezwa kwenye safari zako.',
    'm.claimNotFound': 'Hakuna nafasi inayolingana na kumbukumbu na barua pepe hiyo.',
    'm.signInToSee': 'Ingia ili kuona safari zako',
    'm.signInToSeeHint': 'Nafasi zako zimehifadhiwa pamoja na akaunti yako, katika kifaa chochote.',
    'a.backToSignIn': 'Rudi kuingia',
    'a.emailLink': 'Nitumie kiungo',
    'a.saveNew': 'Hifadhi nenosiri jipya',
    'a.signsOut': 'Kubadilisha nenosiri hukutoa kila mahali pengine.',
    'd.report': 'Ripoti tatizo',
    'd.addMessage': 'Ongeza ujumbe',
    'd.about': 'Ni kuhusu nini?',
    'd.subject': 'Mada',
    'd.tellThem': 'Waeleze kilichotokea',
    'd.newRequest': 'Ombi jipya',
    'd.wentWrong': 'Kuna hitilafu',
    'd.somethingElse': 'Kitu kingine',
    'd.sendRequest': 'Tuma ombi',
    'd.requestRefund': 'Omba marejesho',
    'p.cancellations': 'Kughairi na marejesho',
    'p.questions': 'Maswali',
    'p.findingBooking': 'Kupata nafasi',
    'a.account': 'Akaunti: {name}',
    'a.signInShort': 'Weka safari zako pamoja',
    'n.openMenu': 'Fungua menyu',
    'n.closeMenu': 'Funga menyu',
    'pp.getReminders': 'Pata vikumbusho vya safari',
    'pp.turnOn': 'Washa vikumbusho',
    'pp.notNow': 'Si sasa',
    'pp.willRemind': 'Utapata kikumbusho kabla ya kuondoka.',
    'mt.noSite': 'Hakuna tovuti ya kuweka nafasi kwenye anwani hii',
    'mt.checkAddress': 'Angalia anwani ya wavuti, au tumia kiungo ulichopewa na kampuni yako ya basi.',
    'mt.offline': 'Huna mtandao',
    'mt.offlineHint': 'Unganisha kwenye intaneti ili kupakia tovuti hii kwa mara ya kwanza.',
    'mt.paused': 'Kuweka nafasi kumesimamishwa kwa sasa. Tikiti ulizonazo bado ni halali.',
    'mt.checkBack': 'Tafadhali rudi hivi karibuni.',
    'mt.beingSetUp': 'Tovuti hii ya kuweka nafasi bado inaandaliwa.',
  },

  ln: {
    'ps.closeSafe': 'Okoki kokanga lokasa oyo — tike na yo ekotindama na imeli ata bongo.',
    'ps.confirmed': 'Bakiti na yo ekomami',
    'ps.confirmedHint': 'Tike na yo ezali na nse, mpe kopi ezali koya na imeli na yo.',
    'p.cancelPolicy': 'Okoki komilongola tii bangonga {hours} liboso ya kokende, mpe mbongo ezongi na nzela na yo ya liboso ya kofuta. Soki ntango ya kokende ekomi pene, fungola tike na yo mpe poná {action} — {operator} akotala yango.',
    'p.unpaidReleased': 'Bokomisi oyo efutami te etikami nsima ya miniti {minutes} mpo bakiti ezonga na boteki.',
    't.timesLocal': 'Bangonga ezali ya esika ya kokende ({tz}), ya telefone na yo te.',
    'm.linkSent': 'Soki bokomisi wana ezali, totindi lien mpo na kotala yango. Lien esalaka miniti 15.',
    'm.guestHint': 'Kotisa nimero ya bokomisi na yo mpe imeli, mpe tokotindela yo lien ya tike na yo.',
    'a.resetSent': 'Soki adrɛsi wana ezali na kɔntɛ, totindi lien mpo na kopona motindo ya sika. Esalaka mbala moko mpe esili nsima ya ngonga moko.',
    'a.linkIncomplete': 'Lien oyo ekoki te. Fungola lien uta na imeli na yo, to senga mosusu.',
    'a.yourAccount': 'Kɔntɛ na yo',
    'p.contact': 'Benga {operator}',
    'h.footer': 'Bangonga ya {tz} · Ntalo na {currency} · Esalemi na {operator}',
    'p.faresTimes': 'Ntalo na {currency} · bangonga ya {tz}',
    'c.from': 'Banda',
    'c.to': 'Tii',
    'c.date': 'Mokolo',
    'c.passengers': 'Bapaya',
    'c.selectStop': 'Poná esika',
    'c.searchDepartures': 'Luká mibembo',
    'c.noRoutes': 'Nzela moko ebimisami naino te. Zongá mosika te.',
    'c.email': 'Imeli',
    'c.password': 'Motindo ya kokota',
    'c.yourName': 'Nkombo na yo',
    'c.phone': 'Telefone',
    'c.seats': 'Bakiti',
    'c.paid': 'Efutami',
    'c.continue': 'Kokoba',
    'c.send': 'Kotinda',
    'c.signIn': 'Kokota',
    'c.signOut': 'Kobima',
    'c.about': 'Na tina na',
    'c.help': 'Lisungi',
    'c.myTrips': 'Mibembo na ngai',
    'c.departures': 'Mibembo',
    'c.language': 'Monɔkɔ',
    'c.loading': 'Ezali kofungwama',
    'h.whereTo': 'Ozali kokende wapi?',
    'r.finding': 'Ezali koluka mibembo',
    'r.unavailable': 'Mibembo ezali te',
    'r.none': 'Mobembo moko emonani te',
    'r.changeSearch': 'Bongola boluki na yo',
    's.loading': 'Ezali kofungola bakiti',
    's.seatMap': 'Karte ya bakiti',
    's.choose': 'Poná bakiti na yo',
    's.front': 'Liboso ya bisi',
    's.available': 'Ezali',
    's.yours': 'Ya yo',
    's.taken': 'Ezwami',
    'k.checkout': 'Lifuti',
    'k.whoTravelling': 'Nani azali kokende?',
    'k.contact': 'Bokutani',
    'k.fullName': 'Nkombo mobimba',
    'k.emailHint': 'Totindaka tike na yo awa, mpe okosengela na yango mpo na kozwa bokomisi na yo na nsima.',
    'k.phoneHint': 'Ezali na ntina makasi te. Esalelami kaka soki likambo ezali na mobembo na yo.',
    'k.payConfirm': 'Futa mpe ndima',
    'k.opening': 'Ezali kofungola lokasa ya lifuti',
    'k.takingYou': 'Tozali komema yo na Baraka Pay',
    'k.handoverHint': 'Bakiti na yo ebombami. Okofuta na lokasa ya Baraka Pay, mpe bokomisi oyo ekobongwana ntango lifuti endimami.',
    'k.continueToPay': 'Kokoba na Baraka Pay',
    'k.confirming': 'Ezali kondima lifuti na yo',
    'k.confirmingHint': 'Mbala mingi ezwaka basegonde moke. Tiká lokasa oyo polele.',
    't.loading': 'Ezali kofungola tike na yo',
    't.payFailed': 'Lifuti wana esalemi te',
    't.payCancelled': 'Lifuti elongolami',
    't.tripCancelled': 'Mobembo oyo elongolami',
    't.bookingCancelled': 'Bokomisi elongolami',
    't.stillHeld': 'Bakiti na yo ezali naino ebombami. Okoki komeka lisusu liboso ntango esila.',
    't.heldLonger': 'Bakiti na yo ebombami mwa ntango lisusu soki olingi kosilisa kofuta.',
    't.autoUpdates': 'Lokasa oyo emibongolaka yango moko soki lifuti na yo endimami.',
    't.delayIncluded': 'Bangonga oyo ezali awa na nse esangisi deja retard.',
    't.codeAfterPay': 'Kodi na yo ya komata ekomonana soki lifuti endimami.',
    't.offlineTicket': 'Tike ebombami — ozali na internet te',
    't.offlineAsOf': 'Ba code na yo ya komata ezali kosala naino. Tokokaki kotala état ya mobembo te; oyo nde toyebaki na {when}.',
    't.offlineNoStatus': 'Ba code na yo ya komata ezali kosala naino, kasi tokokaki kotala état ya mobembo te.',
    't.offlineCodeHidden': 'Kangisa internet mpo na komonisa code na yo ya komata.',
    't.cancelBooking': 'Kolongola bokomisi oyo',
    't.noLongerCancel': 'Bokomisi oyo ekoki lisusu kolongolama na Internet te.',
    't.reportProblem': 'Senga kozongisa mbongo to yebisa likambo',
    'm.loading': 'Ezali kofungola mibembo na yo',
    'm.none': 'Bokomisi ezali naino te',
    'm.noneHint': 'Soki okomisi mobembo, ekomonana awa.',
    'm.yourBookings': 'Bokomisi na yo',
    'm.upcoming': 'Oyo ekoya',
    'm.past': 'Oyo eleki',
    'm.findDeparture': 'Luká mobembo',
    'm.guest': 'Okomisaki lokola mopaya mpamba?',
    'm.emailTicket': 'Tindela ngai tike na ngai',
    'm.addToCalendar': 'Bakisa na kalandriye',
    'm.bookAgain': 'Kozwa lisusu',
    'm.ticketSent': 'Tike etindami — tala email na yo.',
    'm.ticketSendFailed': 'Kotinda elongi te. Meka lisusu.',
    'm.sending': 'Ezali kotinda…',
    'm.emailTicketAction': 'Tinda tike',
    'm.viewTicket': 'Tala tike',
    'm.pastTrips': 'Mibembo eleki',
    'm.pastTripsHint': 'Mibembo oyo osili kosala.',
    'm.sendTo': 'Tinda tike oyo epai ya',
    'm.recipientAccount': 'compte na yo',
    'm.recipientBooking': 'na réservation oyo',
    'm.recipientSomeoneElse': 'Moto mosusu',
    'm.recipientEmail': 'Adresse e-mail',
    'm.send': 'Tinda tike',
    'm.cancel': 'Tika',
    'm.sentTo': 'Etindami epai ya {to}',
    'm.recipientNeeded': 'Koma adresse e-mail.',
    'm.signInToForward': 'Kota na compte oyo esalaki réservation oyo mpo na kotinda tike epai ya moto mosusu.',
    'tr.title': 'Bato ya mobembo',
    'tr.heading': 'Bato oyo ozali kobombela',
    'tr.hint': 'Bomba bato oyo ozali kokende na bango; nkombo na bango ekokota na tango ya kofuta.',
    'tr.none': 'Moto moko te abombami',
    'tr.noneHint': 'Bakisa moto awa na nse mpe nkombo na ye ekozala pene mbala ekoya.',
    'tr.add': 'Bakisa moto',
    'tr.save': 'Bomba',
    'tr.remove': 'Longola',
    'tr.optional': 'Ezali na ntina makasi te',
    'tr.idNumber': 'Nimero ya karte',
    'tr.idHint': 'Ezali na ntina makasi te. Soki kaka kompanyi esengi yango.',
    'tr.saveFailed': 'Kobomba elongi te. Meka lisusu.',
    'tr.signInNeeded': 'Kota mpo na kobomba bato',
    'tr.signInHint': 'Bato babombami bazali ya compte na yo.',
    'tr.email': 'E-mail',
    'tr.emailHint': 'Ezali na ntina te. Esalelamaka kaka mpo na kotindela ye tike.',
    'm.reference': 'Nimero ya bokomisi',
    'm.checkEmail': 'Talá imeli na yo',
    'a.min10': 'Ata balɛtrɛ 10.',
    'a.reset': 'Bongisa motindo na yo ya kokota',
    'a.chooseNew': 'Poná motindo ya sika',
    'a.newPassword': 'Motindo ya sika',
    'a.createAccount': 'Kosala kɔntɛ',
    'a.create': 'Kosala kɔntɛ',
    'a.forgot': 'Obosani motindo na yo?',
    'a.guestCheckout': 'Komisa na kɔntɛ te',
    'a.keepLooking': 'Koluka lisusu',
    'ip.title': 'Bakisa {app} na ekran na yo ya ebandeli',
    'pr.title': 'Profil na yo',
    'k.promoCode': 'Kodi ya bokitisi (soki ozali na yango)',
    'k.promoHint': 'Ozali na kodi? Kotisa yango mpe bokitisi ekosalema na bokomisi oyo.',
    'o.title': 'Makabo',
    'o.yours': 'Makabo na yo',
    'o.yoursHint': 'Bakodi osili kozwa, mpe mibembo oyo ezali naino kozipa.',
    'o.available': 'Ezali sikoyo',
    'o.availableHint': 'Zwá kodi, ekozela yo mbala ekoya ya bokomisi.',
    'o.none': 'Likabo ezali naino te',
    'o.noneHint': 'Soki mosali abandi bokitisi, ekomonana awa.',
    'o.claim': 'Zwá kodi',
    'o.claimAction': 'Zwá',
    'o.claimed': 'Ezwami. Ekosalema na bokomisi na yo oyo elandi.',
    'o.claimFailed': 'Kodi wana ekokaki kozwama te.',
    'o.ridesLeft': 'Mibembo {count} etikali',
    'o.rideLeft': 'Mobembo 1 etikali',
    'o.until': 'Kino {date}',
    'o.expires': 'Salelá liboso ya {date}',
    'o.refer': 'Benga mpe zwá',
    'o.referHint': 'Kabola kodi na yo. Soki moninga asali mobembo ya liboso, bino mibale bokozwa.',
    'o.yourCode': 'Kodi na yo',
    'o.copy': 'Kopi',
    'o.copied': 'Ekopiami',
    'o.waiting': '{count} bakomi, bakei naino te',
    'o.earned': '{count} ezwami',
    's.swap': 'Kobongola esika ya kobanda na ya kosuka',
    's.today': 'Lelo',
    'm.nextDeparture': 'Mobembo ya sima',
    'm.moreUpcoming': 'Mosusu {count} ezali koya',
    's.tomorrow': 'Lobi',
    's.errChooseBoth': 'Poná esika okobanda mpe esika okokende.',
    's.errSameStop': 'Esika ya kobanda mpe ya kosuka esengeli ekesana.',
    'pr.you': 'Yo',
    'pr.phone': 'Nimero ya telefone',
    'pr.dob': 'Mokolo ya mbotama',
    'pr.idNumber': 'Nimero ya karte to pasapɔrɔ',
    'pr.idHint': 'Basali mosusu basɛngaka yango na komata.',
    'pr.address': 'Adrɛsi',
    'pr.line1': 'Adrɛsi ya balabala',
    'pr.line2': 'Ndako, biro (soki ezali)',
    'pr.city': 'Engumba',
    'pr.region': 'Provins to etuka',
    'pr.postalCode': 'Kodi postal',
    'pr.country': 'Ekólo',
    'pr.countryHint': 'Balɛtrɛ mibale, ndakisa CD.',
    'pr.emergency': 'Moto ya lisalisi',
    'pr.emergencyHint': 'Nani kobenga soki likambo esalemi na mobembo.',
    'pr.emergencyName': 'Nkombo na ye',
    'pr.emergencyPhone': 'Nimero na ye ya telefone',
    'pr.save': 'Kobomba profil',
    'pr.saved': 'Profil ebombami.',
    'pr.saveFailed': 'Tokokaki kobomba profil na yo te. Meká lisusu.',
    'pr.addPhoto': 'Bakisa foto',
    'pr.changePhoto': 'Kobongola foto',
    'pr.removePhoto': 'Kolongola',
    'pr.photoFailed': 'Foto wana ekokaki kosalema te. Meká PNG to JPEG na se ya 2 MB.',
    'pr.signOut': 'Kobima',
    'ip.why': 'Efungoli mbala moko batike na yo, mpe esalaka ata na réseau te.',
    'ip.install': 'Kotia',
    'ip.notNow': 'Sikoyo te',
    'ip.iosShare': 'Finá bouton Share na barre ya navigateur.',
    'ip.iosAdd': 'Poná “Add to Home Screen”.',
    'a.signInLead': 'Kota mpo na kokomisa mpe kobomba mibembo na yo na esaleli nyonso.',
    'a.registerLead': 'Osengeli na kɔntɛ mpo na kokomisa. Ezali kozwa ntango moke.',
    'a.haveAccount': 'Nazali déjà na kɔntɛ',
    'm.claim': 'Bakisa bokomisi ya kala',
    'm.claimHint': 'Okomisaki liboso ya kozala na kɔntɛ? Kotisa nimero mpe email esalelamaki.',
    'm.claimAction': 'Bakisa na mibembo na ngai',
    'm.claimed': 'Ebakisami na mibembo na yo.',
    'm.claimNotFound': 'Bokomisi moko te ekokani na nimero mpe email wana.',
    'm.signInToSee': 'Kota mpo na komona mibembo na yo',
    'm.signInToSeeHint': 'Bokomisi na yo ebombami na kɔntɛ na yo, na esaleli nyonso.',
    'a.backToSignIn': 'Zongá na kokota',
    'a.emailLink': 'Tindela ngai lien',
    'a.saveNew': 'Bomba motindo ya sika',
    'a.signsOut': 'Kobongola motindo na yo ebimisi yo bisika mosusu nyonso.',
    'd.report': 'Yebisa likambo',
    'd.addMessage': 'Bakisa nsango',
    'd.about': 'Ezali na tina na nini?',
    'd.subject': 'Motó ya likambo',
    'd.tellThem': 'Yebisa bango likambo nini esalemaki',
    'd.newRequest': 'Bosengi ya sika',
    'd.wentWrong': 'Likambo esalemi mabe',
    'd.somethingElse': 'Likambo mosusu',
    'd.sendRequest': 'Tinda bosengi',
    'd.requestRefund': 'Senga kozongisa mbongo',
    'p.cancellations': 'Kolongola mpe kozongisa mbongo',
    'p.questions': 'Mituna',
    'p.findingBooking': 'Kozwa bokomisi',
    'a.account': 'Kɔntɛ: {name}',
    'a.signInShort': 'Batela mibembo na yo esika moko',
    'n.openMenu': 'Kofungola menu',
    'n.closeMenu': 'Kokanga menu',
    'pp.getReminders': 'Zwá bokundoli ya mobembo',
    'pp.turnOn': 'Fungola bokundoli',
    'pp.notNow': 'Sikoyo te',
    'pp.willRemind': 'Okozwa bokundoli liboso ya kokende.',
    'mt.noSite': 'Site ya bokomisi ezali te na adrɛsi oyo',
    'mt.checkAddress': 'Talá adrɛsi, to salelá lien oyo kompani na yo ya bisi epesaki yo.',
    'mt.offline': 'Ozali na Internet te',
    'mt.offlineHint': 'Kangisa na Internet mpo na kofungola site oyo mbala ya liboso.',
    'mt.paused': 'Bokomisi etelemi sikoyo. Batike oyo ozali na yango ezali kaka malamu.',
    'mt.checkBack': 'Svp zongá mosika te.',
    'mt.beingSetUp': 'Site oyo ya bokomisi ezali naino kobongisama.',
  },

} as const;

/**
 * Every locale must carry every key. A passenger buying a ticket cannot work
 * around a missing string the way a member of staff can, so this is a compile
 * error rather than something discovered in front of one.
 */
const _everyLocaleIsComplete: Record<LocaleCode, Record<StringKey, string>> = dict;
void _everyLocaleIsComplete;
